import React from "react";
import { useNavigate } from "react-router-dom";
import { useBranding } from "@/lib/branding";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export default function Privacy() {
  const b = useBranding();
  const navigate = useNavigate();
  const uni = b.university_name || "our institution";
  const org = `${uni} Alumni Association`;

  const Section = ({ title, children }) => (
    <div className="mb-6">
      <h2 className="font-serif text-lg text-primary mb-2">{title}</h2>
      <div className="text-sm text-muted-foreground space-y-2 leading-relaxed">{children}</div>
    </div>
  );

  // Render an admin-authored custom notice: blank lines split paragraphs; a short line
  // without terminal punctuation is treated as a section heading.
  const renderCustom = (text) => {
    const blocks = text.replace(/\r\n/g, "\n").split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
    return blocks.map((block, i) => {
      const lines = block.split("\n");
      const first = lines[0].trim();
      const isHeading = lines.length === 1 && first.length <= 60 && !/[.!?:]$/.test(first);
      if (isHeading) {
        return <h2 key={i} className="font-serif text-lg text-primary mb-2 mt-6 first:mt-0">{first}</h2>;
      }
      return <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-3">{block.split("\n").map((ln, j) => <React.Fragment key={j}>{ln}{j < lines.length - 1 ? <br /> : null}</React.Fragment>)}</p>;
    });
  };

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <button data-testid="privacy-back" onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-7 w-7 text-primary" />
          <h1 className="font-serif text-3xl tracking-tight">Privacy Notice</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">
          How the {org} collects, uses, and protects your personal information.
        </p>

        {b.privacy_notice ? renderCustom(b.privacy_notice) : (<>
        <Section title="Who we are">
          <p>This alumni registry is operated by the {org}. We maintain it to keep our alumni
          community connected, share news and events, and support the mission of {uni}.</p>
        </Section>

        <Section title="What information we hold">
          <p>We keep contact details (name, email, phone, mailing address), your class and degree
          information, and records of your engagement with us (event attendance, communications, and
          any gifts). Some of this comes from your student and advancement records; some you provide
          yourself when you claim and update your profile.</p>
        </Section>

        <Section title="How we use it">
          <p>We use your information to communicate with you, invite you to events and reunions,
          keep your record accurate, and — only if you allow it — to share appropriate fundraising
          and giving opportunities. Our legal basis is our legitimate interest in maintaining a
          relationship with our alumni, always subject to your right to opt out.</p>
        </Section>

        <Section title="Your choices and rights">
          <p>You control your communications. From your profile or any email we send, you can opt out
          of all email, or of marketing and solicitation email specifically, at any time. You may also
          request a copy of the data we hold about you, ask us to correct it, or ask us to delete it.
          To make any of these requests, contact the alumni office.</p>
        </Section>

        <Section title="Sharing">
          <p><strong>We never sell or share your personal information</strong> with unrelated third
          parties. We use a small number of trusted service providers (for email delivery, payment
          processing, and hosting) solely to operate this registry, and only under agreements that
          require them to protect your data.</p>
        </Section>

        <Section title="Security">
          <p>Your data is stored in a dedicated, access-controlled database. Connections are encrypted
          in transit (TLS), passwords are stored using strong one-way hashing, and access is limited to
          authorized alumni-office staff by role.</p>
        </Section>

        {b.privacy_url && (
          <Section title="Full institutional policy">
            <p>
              For our complete institutional privacy policy, see{" "}
              <a href={b.privacy_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                {b.privacy_url}
              </a>.
            </p>
          </Section>
        )}
        </>)}

        <p className="text-xs text-muted-foreground/70 mt-8">
          © {new Date().getFullYear()} {org}. This notice may be updated from time to time.
        </p>
      </div>
    </div>
  );
}
